import type { GenreConfig } from './types';

export const METAL: GenreConfig = {
  id: 'metal',
  siteName: 'MetalAlbumReviews',
  domain: 'metalalbumreviews.com',
  noun: 'metal',
  label: 'Metal',
  shortName: 'MAR',
  tagline: 'In-Depth Metal Album Reviews',
  description:
    'In-depth metal album reviews with a published scorecard: songwriting, sound, performance and cohesion, from heavy metal classics to modern extreme metal.',
  readerLine: 'a serious metal fan',
  xHandle: null,
  gaId: 'G-GSWL29LWL1',
  ahrefsKey: 'QqIfetOEiix3EWRbviPynw',
  clarityId: 'z38q06io33',
  accentHue: 0,

  dailyReviews: 1,
  launchReviews: 20,
  trackLimits: { min: 4, max: 30 },

  musicbrainzTags: ['metal', 'heavy metal', 'thrash metal', 'death metal', 'black metal', 'doom metal', 'progressive metal'],
  wikipediaArtistSuffixes: ['(band)', '(musician)', '(singer)'],
  wikipediaArtistSearchTerm: 'metal band',
  artistKeywordSuffix: 'band',

  scorecard: {
    dimensions: [
      {
        key: 'writing',
        label: 'Songwriting',
        weight: 0.25,
        measures:
          'Riffs, song structure, melody and lyrics: whether the songs are built to last, with ideas that develop instead of repeating.',
      },
      {
        key: 'production',
        label: 'Sound',
        weight: 0.25,
        measures:
          'Guitar tone, drum sound, mix and mastering: how well the record is recorded and how well the sound serves the songs.',
      },
      {
        key: 'delivery',
        label: 'Performance',
        weight: 0.15,
        measures:
          'Musicianship, vocals, tightness and feel: the playing itself, from solos and drumming to the conviction of the vocal.',
      },
      {
        key: 'cohesion',
        label: 'Cohesion',
        weight: 0.15,
        measures:
          'Sequencing, pacing, length and filler: whether it works as an album rather than a pile of songs.',
      },
      {
        key: 'originality',
        label: 'Originality',
        weight: 0.1,
        measures:
          "What it adds, measured against the band's own catalog and the peers of its moment, not against what came later.",
      },
      {
        key: 'replay',
        label: 'Replay value',
        weight: 0.1,
        measures:
          'How it holds up on repeat listens and over time. For recent releases this is a projection and the note says so.',
      },
    ],
    topBandMeaning: 'Among the best metal albums ever made on this measure.',
  },

  canonicalGenres: [
    'Heavy Metal',
    'Thrash Metal',
    'Death Metal',
    'Black Metal',
    'Doom Metal',
    'Progressive Metal',
    'Power Metal',
    'Sludge Metal',
    'Metalcore',
    'Nu Metal',
    'Groove Metal',
    'Post-Metal',
  ],

  primaryGenres: [
    { label: 'Heavy Metal', slug: 'heavy-metal', hue: 0, styles: ['nwobhm', 'traditional-heavy-metal', 'speed-metal', 'hard-rock'] },
    { label: 'Thrash', slug: 'thrash-metal', hue: 20, styles: ['bay-area-thrash', 'teutonic-thrash', 'crossover-thrash', 'technical-thrash'] },
    { label: 'Death Metal', slug: 'death-metal', hue: 350, styles: ['technical-death-metal', 'melodic-death-metal', 'brutal-death-metal', 'old-school-death-metal', 'death-doom'] },
    { label: 'Black Metal', slug: 'black-metal', hue: 260, styles: ['second-wave-black-metal', 'atmospheric-black-metal', 'symphonic-black-metal', 'blackgaze', 'raw-black-metal'] },
    { label: 'Doom', slug: 'doom-metal', hue: 280, styles: ['traditional-doom', 'funeral-doom', 'stoner-metal', 'gothic-metal'] },
    { label: 'Progressive', slug: 'progressive-metal', hue: 200, styles: ['djent', 'avant-garde-metal', 'technical-metal', 'mathcore'] },
    { label: 'Power Metal', slug: 'power-metal', hue: 45, styles: ['symphonic-metal', 'folk-metal', 'neoclassical-metal', 'viking-metal'] },
    { label: 'Sludge', slug: 'sludge-metal', hue: 90, styles: ['southern-metal', 'stoner-doom', 'drone-metal'] },
    { label: 'Metalcore', slug: 'metalcore', hue: 320, styles: ['melodic-metalcore', 'deathcore', 'post-hardcore', 'grindcore'] },
    { label: 'Nu Metal', slug: 'nu-metal', hue: 170, styles: ['alternative-metal', 'rap-metal', 'industrial-metal'] },
    { label: 'Groove Metal', slug: 'groove-metal', hue: 10, styles: ['post-thrash', 'neo-thrash'] },
    { label: 'Post-Metal', slug: 'post-metal', hue: 220, styles: ['atmospheric-sludge', 'post-black-metal', 'experimental-metal'] },
  ],

  genreDescriptions: {
    'heavy-metal':
      'The foundation of the genre: loud guitars, driving rhythms and big vocals. Heavy metal grew out of late-1960s hard rock and took shape with Black Sabbath in Birmingham, then was sharpened by Judas Priest and the New Wave of British Heavy Metal led by Iron Maiden. Everything else on this site descends from it.',
    'thrash-metal':
      'Fast, precise and aggressive. Thrash took the speed of hardcore punk and the riffing of the NWOBHM in the early 1980s, with Metallica, Slayer, Megadeth and Anthrax setting the template and the Bay Area and German scenes building on it.',
    'death-metal':
      'Downtuned guitars, blast beats and growled vocals. Death metal emerged in the mid-1980s from Florida bands such as Death and Morbid Angel and spread to Sweden, New York and beyond, splitting into technical, melodic and brutal branches.',
    'black-metal':
      'Tremolo-picked riffs, shrieked vocals and an atmosphere that often matters more than the mix. The second wave formed in early-1990s Norway with Mayhem, Darkthrone, Burzum and Emperor, and later bands pushed it toward atmospheric, symphonic and shoegaze-adjacent forms.',
    'doom-metal':
      'Slow tempos, heavy riffs and a sense of weight. Doom takes its cue from early Black Sabbath, through Pentagram, Saint Vitus and Candlemass, and stretches from traditional doom to funeral doom and stoner metal.',
    'progressive-metal':
      'Complex structures, odd meters and long-form songwriting. Progressive metal grew from Queensrÿche, Fates Warning and Dream Theater and now covers everything from Opeth and Tool to Meshuggah and the djent bands that followed them.',
    'power-metal':
      'Fast tempos, soaring melodic vocals and big choruses. Power metal was shaped by Helloween in Germany and spread across Europe, with symphonic and folk offshoots that lean on keyboards, orchestration and mythology.',
    'sludge-metal':
      'Doom heaviness with hardcore abrasion. Sludge took shape in late-1980s and 1990s Louisiana and Georgia with Eyehategod, Crowbar and later Mastodon, and sits close to stoner and southern metal.',
    metalcore:
      'Hardcore punk structure and breakdowns fused with metal riffing. Metalcore developed through the 1990s and became one of the dominant heavy sounds of the 2000s, later branching into deathcore and its melodic forms.',
    'nu-metal':
      'Downtuned grooves, hip-hop rhythms and alternative-rock songwriting. Nu metal broke through in the late 1990s with Korn, Deftones, Slipknot and Linkin Park and reached a mainstream audience no metal style had before.',
    'groove-metal':
      'Mid-tempo, syncopated riffs built for impact rather than speed. Groove metal took shape in the early 1990s with Pantera, Sepultura and Machine Head as thrash slowed down and got heavier.',
    'post-metal':
      'Long builds, layered textures and dynamics borrowed from post-rock. Post-metal grew out of Neurosis and Isis and treats heaviness as one tool among many.',
  },

  genreGuides: {
    'heavy-metal': {
      sound:
        'Heavy metal albums are built on guitar riffs, a strong rhythm section and a vocalist who can carry over both. The classic sound uses twin guitars, gallop rhythms and big melodic choruses. This tag is the broad umbrella every album on the site sits under; the narrower genre pages split it by era and style.',
      listenFor:
        'Our scores weight songwriting and sound most heavily, at 25% each, because a metal record lives or dies on its riffs and on whether the recording lets them hit. Albums that sound huge but repeat the same idea land in the middle bands; albums with great songs and a thin mix do too.',
    },
    'thrash-metal': {
      sound:
        'Thrash is built on fast down-picked riffs, aggressive drumming and shouted or snarled vocals, with solos used as punctuation. Song structures are often longer and more sectional than in traditional heavy metal.',
      listenFor:
        'Speed alone does not score. The scorecard looks at whether the riffs are memorable, whether the tempo changes serve the song, and whether the album keeps its intensity without blurring into sameness.',
    },
    'death-metal': {
      sound:
        'Death metal uses low tunings, fast tremolo and chromatic riffs, blast beats and growled vocals. Technical bands push musicianship forward; old-school bands value atmosphere and grime; melodic bands bring in harmonized leads.',
      listenFor:
        'Performance matters here, but the scorecard asks whether the technique builds songs. Clear, heavy production scores better than either a muddy mix or an overcompressed one that flattens the dynamics.',
    },
    'black-metal': {
      sound:
        'Black metal is built on tremolo-picked melodies, fast drumming and shrieked vocals. Lo-fi production is often a deliberate choice, and atmosphere and repetition are part of the design.',
      listenFor:
        'The scorecard judges sound against intent: raw production is not a flaw when it serves the record. Albums that turn atmosphere into memorable songs score highest; those that rely on mood alone lose points on songwriting and replay.',
    },
  },

  styleExamples: [
    'nwobhm', 'bay area thrash', 'melodic death metal', 'technical death metal', 'atmospheric black metal',
    'funeral doom', 'stoner metal', 'djent', 'symphonic metal', 'deathcore', 'industrial metal', 'blackgaze',
  ],
  shortGenreSuffixes: [' Metal'],

  prompt: {
    evidenceExamples: 'a riff, a solo, a vocal take, a drum pattern, a sequencing decision',
    fillerTracks: 'intros, interludes and instrumental segues',
    analysisFocus: 'songwriting, sound and performance. Name producers and engineers only from the FACT SHEET.',
    primaryKeywordExample: '"Metallica Master of Puppets review" or "Opeth Blackwater Park review"',
    secondaryKeywordExample: '"Master of Puppets album analysis", "best thrash metal albums", "Metallica third album"',
    headingExamples:
      '"Metallica Master of Puppets — Thrash Learns to Write Symphonies", "Opeth Blackwater Park — Where Death Metal Found Its Patience"',
  },

  voices: [
    {
      name: 'Ingrid',
      title: 'Lineage Voice',
      initials: 'I',
      bio: 'The house voice for metal history: where an album sits in the lineage from Black Sabbath onward, who it learned from and who learned from it.',
      specialization: 'Lineage',
      featured: true,
      avatarHue: 0,
      lens: `LENS: Lineage
Place the album in metal history: what it answered, which bands it learned from, which bands learned from it.
Voice: direct, confident, willing to disagree with the consensus.`,
      match: {
        artists: ['black sabbath', 'judas priest', 'iron maiden', 'motörhead', 'motorhead', 'dio', 'rainbow', 'deep purple', 'ozzy osbourne', 'saxon', 'accept', 'scorpions', 'def leppard', 'diamond head', 'mercyful fate', 'king diamond', 'manowar', 'w.a.s.p.', 'dokken', 'ratt'],
        labels: ['vertigo', 'emi', 'neat records', 'roadrunner'],
        keywords: ['nwobhm', 'heavy metal', 'classic', 'birmingham', 'traditional'],
      },
    },
    {
      name: 'Dag',
      title: 'Extreme Metal Voice',
      initials: 'D',
      bio: 'Listens from the extreme end: death metal, black metal and grindcore, and how the underground pushed heaviness further.',
      specialization: 'Extreme',
      featured: true,
      avatarHue: 350,
      lens: `LENS: The extreme end
Hear the album through death metal, black metal and grindcore: how far it pushes speed, heaviness and atmosphere, and whether the extremity serves the songs.
Voice: vivid and exact about how the music hits.`,
      match: {
        artists: ['death', 'morbid angel', 'cannibal corpse', 'obituary', 'deicide', 'suffocation', 'entombed', 'dismember', 'carcass', 'napalm death', 'bolt thrower', 'autopsy', 'immolation', 'incantation', 'nile', 'behemoth', 'gojira', 'blood incantation', 'cryptopsy', 'pig destroyer'],
        labels: ['earache', 'nuclear blast', 'relapse', 'century media', 'peaceville'],
        keywords: ['death metal', 'grindcore', 'brutal', 'blast', 'florida', 'stockholm'],
      },
    },
    {
      name: 'Felix',
      title: 'Sound & Production Voice',
      initials: 'F',
      bio: 'Listens production-first: guitar tone, drum sound, the mix and the loudness war, and the engineers who shaped how metal sounds.',
      specialization: 'Sound',
      featured: true,
      avatarHue: 160,
      lens: `LENS: Sound first
Listen to the album as a recording: guitar tone, drum sound, the bass in the mix, compression and dynamics, the room. Treat producers and engineers named in the FACT SHEET as co-authors.
Voice: precise and practical, technical without jargon for its own sake.`,
      match: {
        artists: ['meshuggah', 'devin townsend', 'strapping young lad', 'fear factory', 'killswitch engage', 'periphery', 'architects', 'bring me the horizon', 'sleep token'],
        labels: ['sumerian', 'inside out'],
        keywords: ['production', 'producer', 'remaster', 'mix', 'tone', 'sound'],
      },
    },
    {
      name: 'Lena',
      title: 'Riff & Composition Voice',
      initials: 'L',
      bio: 'Reads the songwriting closely: riffs, structure, transitions and how a song builds, and whether the ideas develop or just repeat.',
      specialization: 'Composition',
      featured: true,
      avatarHue: 280,
      lens: `LENS: The songwriting
Study how the songs are built: riffs, structure, transitions, melody, and where a song develops an idea instead of restating it. Explain the mechanics of what works and what doesn't.
Voice: incisive and analytical, never decorative.`,
      match: {
        artists: ['metallica', 'megadeth', 'testament', 'iron maiden', 'mastodon', 'gojira', 'opeth', 'in flames', 'dark tranquillity', 'at the gates', 'amon amarth', 'trivium', 'machine head'],
        labels: ['elektra', 'megaforce', 'combat'],
        keywords: ['riff', 'songwriting', 'composition', 'melodic'],
      },
    },
    {
      name: 'Rex',
      title: 'Thrash & Speed Voice',
      initials: 'R',
      bio: 'Covers thrash, speed and traditional metal: the Bay Area, Germany and the bands who made fast and precise the standard.',
      specialization: 'Thrash',
      featured: false,
      avatarHue: 30,
      lens: `LENS: Thrash and speed
Hear the album against thrash and speed metal: the Bay Area, Germany, New York crossover. Know what is earned and what is borrowed from the big four.
Voice: unhurried, precise, comparative.`,
      match: {
        artists: ['slayer', 'metallica', 'megadeth', 'anthrax', 'exodus', 'testament', 'overkill', 'kreator', 'sodom', 'destruction', 'sepultura', 'annihilator', 'dark angel', 'death angel', 'vio-lence', 'forbidden', 'nuclear assault', 'municipal waste', 'power trip', 'havok'],
        labels: ['metal blade', 'megaforce', 'noise records', 'combat', 'def american'],
        keywords: ['thrash', 'speed metal', 'bay area', 'crossover', 'teutonic'],
      },
    },
    {
      name: 'Sigrid',
      title: 'Global Scenes Voice',
      initials: 'S',
      bio: 'Follows metal outside the US and UK: Scandinavia, Germany, Brazil, Japan and Eastern Europe, and what each scene added.',
      specialization: 'Global',
      featured: false,
      avatarHue: 180,
      lens: `LENS: Global scenes
Position the album in the wider metal world: Norway, Sweden, Finland, Germany, Brazil, Japan, Eastern Europe. Notice what is local and what travels.
Voice: comparative and border-crossing.`,
      match: {
        artists: ['mayhem', 'darkthrone', 'burzum', 'emperor', 'immortal', 'enslaved', 'bathory', 'satyricon', 'gorgoroth', 'nightwish', 'children of bodom', 'sentenced', 'amorphis', 'insomnium', 'helloween', 'blind guardian', 'rammstein', 'sepultura', 'babymetal', 'x japan'],
        labels: ['deathlike silence', 'moonfog', 'spinefarm', 'season of mist'],
        keywords: ['norway', 'norwegian', 'sweden', 'swedish', 'finland', 'finnish', 'german', 'brazil', 'japan', 'black metal', 'viking'],
      },
    },
    {
      name: 'Morgan',
      title: 'Doom & Atmosphere Voice',
      initials: 'M',
      bio: 'Covers the slow and heavy: doom, sludge, stoner and post-metal, where weight, texture and patience do the work.',
      specialization: 'Doom',
      featured: false,
      avatarHue: 310,
      lens: `LENS: Weight and atmosphere
Hear the album through doom, sludge, stoner and post-metal: how it uses tempo, repetition, texture and dynamics to build weight.
Voice: patient and exact; never confuse slowness with depth.`,
      match: {
        artists: ['candlemass', 'saint vitus', 'pentagram', 'trouble', 'cathedral', 'electric wizard', 'sleep', 'kyuss', 'neurosis', 'isis', 'cult of luna', 'eyehategod', 'crowbar', 'down', 'yob', 'pallbearer', 'my dying bride', 'paradise lost', 'type o negative', 'sunn o)))'],
        labels: ['southern lord', 'rise above', 'hydra head', 'neurot'],
        keywords: ['doom', 'sludge', 'stoner', 'post-metal', 'drone', 'gothic'],
      },
    },
    {
      name: 'Kai',
      title: 'Modern Era Voice',
      initials: 'K',
      bio: 'Covers metal since 2000: metalcore, deathcore, djent and nu metal, and is the voice most likely to push back on the canon.',
      specialization: 'Modern',
      featured: false,
      avatarHue: 50,
      lens: `LENS: The modern era
Test the canon instead of deferring to it. Understand how metalcore, nu metal, djent and streaming changed the genre; judge new sounds on their own terms.
Voice: sharp, contrarian when the evidence supports it.`,
      match: {
        artists: ['slipknot', 'korn', 'deftones', 'system of a down', 'linkin park', 'limp bizkit', 'avenged sevenfold', 'bullet for my valentine', 'killswitch engage', 'as i lay dying', 'parkway drive', 'architects', 'bring me the horizon', 'knocked loose', 'spiritbox', 'lorna shore', 'whitechapel', 'currents', 'polyphia', 'jinjer'],
        labels: ['roadrunner', 'rise records', 'sumerian', 'epitaph', 'fearless'],
        keywords: ['metalcore', 'deathcore', 'nu metal', 'djent', 'post-hardcore'],
        yearAfter: 2000,
      },
    },
    {
      name: 'Theo',
      title: 'Progressive & Technical Voice',
      initials: 'T',
      bio: 'Follows progressive and technical metal: long-form structure, odd meters and musicianship, and when ambition pays off.',
      specialization: 'Progressive',
      featured: false,
      avatarHue: 200,
      lens: `LENS: Progressive and technical
Read the album as long-form design: structure across songs, meter, development, musicianship. Ask whether the ambition serves the music or just displays skill.
Voice: strategic and analytical.`,
      match: {
        artists: ['dream theater', 'queensrÿche', 'queensryche', 'fates warning', 'symphony x', 'opeth', 'tool', 'meshuggah', 'cynic', 'atheist', 'between the buried and me', 'haken', 'leprous', 'devin townsend', 'mastodon', 'gojira', 'tesseract', 'animals as leaders', 'ihsahn', 'voivod'],
        labels: ['inside out', 'metal blade', 'sensory'],
        keywords: ['progressive', 'technical', 'concept', 'prog', 'djent'],
      },
    },
    {
      name: 'Vera',
      title: 'Lyrics & Concepts Voice',
      initials: 'V',
      bio: 'Reads albums for what they say: concept records, mythology, politics and the social worlds that produced them.',
      specialization: 'Concepts',
      featured: false,
      avatarHue: 130,
      lens: `LENS: Lyrics and concepts
Read the album as a statement: its concept, themes, imagery and the world that shaped it, and whether the words match the weight of the music. Paraphrase; never quote lyrics.
Voice: contextual and accessible, never academic for its own sake.`,
      match: {
        artists: ['iron maiden', 'queensrÿche', 'king diamond', 'blind guardian', 'rage against the machine', 'napalm death', 'sepultura', 'sabaton', 'ayreon', 'the ocean', 'mastodon'],
        labels: ['century media'],
        keywords: ['concept', 'political', 'mythology', 'war', 'story', 'opera'],
      },
    },
  ],

  gapTemplates: {
    bestOfYear: 'Best Metal Albums of {year}',
    era: 'Metal in the {era}',
    subgenresExplained: 'Metal Subgenres Explained',
  },
};
